import React, { useContext, useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  Landmark,
  Loader2,
  Search,
  Sparkles,
  TextSearch,
  User,
  X,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import api from "~/services/api";
import { formatter } from "~/functions";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import { AlertContext } from "~/App";
import { BalanceBox, BalanceLine } from "../../components/BalanceLines";
import DaysBar from "./DaysBar";
import MatchesModal from "./MatchesModal";
import StatusFilter from "./StatusFilter";
import { conciliationLabel } from "./conciliationLabel";

function safeDate(value) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM/dd/yyyy");
  } catch (err) {
    return "-";
  }
}

export default function StatementView({
  statementId,
  bankAccountId,
  isClosed,
  onBack,
}) {
  const { alertBox } = useContext(AlertContext);
  const [data, setData] = useState({ loaded: false, items: [] });
  const [balance, setBalance] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  const [verifyingAll, setVerifyingAll] = useState(false);
  const [itemStatus, setItemStatus] = useState({});
  const [matchesItem, setMatchesItem] = useState(null);

  async function load(day = activeDay, status = activeStatus) {
    try {
      const params = {};
      if (day) params.date = day;
      if (status) params.status = status;

      const [detail, balanceData] = await Promise.all([
        api.get(`/bankconciliations/${statementId}`, { params }),
        api
          .get(`/bankconciliations/${statementId}/balance`, { params })
          .catch(() => ({ data: null })),
      ]);
      setData({ ...detail.data, loaded: true });
      setBalance(balanceData.data);
    } catch (err) {
      toast(err?.response?.data?.error || "Could not load the statement.", {
        type: "error",
        autoClose: 3000,
      });
    }
  }

  useEffect(() => {
    load(null, null);
  }, [statementId]);

  async function handleSelectDay(day) {
    setActiveDay(day);
    await load(day, activeStatus);
  }

  async function handleSelectStatus(status) {
    setActiveStatus(status);
    await load(activeDay, status);
  }

  async function handleVerifyAll() {
    setVerifyingAll(true);
    try {
      const { data: result } = await api.put(
        `/bankconciliations/${statementId}/verify-all`
      );
      const {
        total = 0,
        verified = 0,
        ambiguous = 0,
        not_found = 0,
      } = result || {};
      toast(
        `${verified} reconciled, ${ambiguous} to review, ${not_found} without match (of ${total}).`,
        {
          type: ambiguous || not_found ? "warning" : "success",
          autoClose: 5000,
        }
      );
      if (ambiguous > 0) {
        await handleSelectStatus("Review");
      } else {
        await load();
      }
    } catch (err) {
      toast(err?.response?.data?.error || "Could not verify the statement.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setVerifyingAll(false);
    }
  }

  async function handleVerifyItem(item) {
    setItemStatus((prev) => ({ ...prev, [item.id]: "loading" }));
    try {
      await api.put(`/bankconciliations-verify/${item.id}`);
      await load();
    } catch (err) {
      const matches = err?.response?.data?.matches;
      if (matches) {
        toast(`${matches} candidates found — choose one.`, {
          type: "info",
          autoClose: 2500,
        });
        setMatchesItem(item);
      } else {
        toast(
          err?.response?.data?.error ||
            "No automatic match — use the search to reconcile manually.",
          { type: "warning", autoClose: 3000 }
        );
      }
    } finally {
      setItemStatus((prev) => ({ ...prev, [item.id]: undefined }));
    }
  }

  function handleUnlink(item) {
    alertBox({
      title: "Attention!",
      descriptionHTML:
        item.status === "OnlyBank"
          ? "Are you sure you want to undo the Only Bank mark of this movement? It will go back to Pending."
          : "Are you sure you want to undo the reconciliation of this movement?",
      buttons: [
        { title: "Cancel", class: "cancel" },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(`/bankconciliations-items/${item.id}/link`);
              toast("Reconciliation undone!", { autoClose: 1000 });
              await load();
            } catch (err) {
              toast(err?.response?.data?.error || "Could not undo.", {
                type: "error",
                autoClose: 3000,
              });
            }
          },
        },
      ],
    });
  }

  const items = data?.items || [];
  const counts = data?.counts || {};
  const reviewCount = Number(counts.review || 0);
  const noMatchCount = Number(counts.pending || 0);
  const openCount = reviewCount + noMatchCount;

  return (
    <InputLineGroup title="MOVEMENTS" activeMenu className="relative">
      {matchesItem ? (
        <MatchesModal
          item={matchesItem}
          bankAccountId={bankAccountId}
          onClose={() => setMatchesItem(null)}
          onConciliated={() => load()}
        />
      ) : null}

      <InputLine>
        <div className="w-full flex flex-row items-center justify-between gap-2 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="border rounded-md p-1 px-2 h-8 flex flex-row items-center justify-center text-xs gap-1 hover:border-primary transition-all"
          >
            <ArrowLeft size={16} /> Back to month
          </button>
          <span className="text-xs text-gray-500">
            {safeDate(data?.date_from)} to {safeDate(data?.date_to)}
          </span>
        </div>
      </InputLine>

      {balance ? (
        <InputLine title="Balance">
          <div className="w-full pt-2">
            <BalanceBox>
              <BalanceLine
                title="Beginning balance"
                value={balance.beginning_balance}
              />
              <BalanceLine title="Credits (IN)" value={balance.credits} />
              <BalanceLine title="Debits (OUT)" value={balance.debits} />
              <BalanceLine
                title="Ending balance"
                value={balance.ending_balance}
              />
              <div className="w-full flex flex-row items-center justify-between text-gray-500 pt-2">
                <span>{counts.all ?? items.length} movements</span>
                <span>{openCount} open</span>
              </div>
            </BalanceBox>
          </div>
        </InputLine>
      ) : null}

      {data?.days?.length ? (
        <InputLine title="Days">
          <DaysBar
            days={data.days}
            activeDay={activeDay}
            onSelect={handleSelectDay}
          />
        </InputLine>
      ) : null}

      <InputLine title="Filter">
        <StatusFilter
          counts={counts}
          activeStatus={activeStatus}
          onSelect={handleSelectStatus}
        />
      </InputLine>

      {!isClosed && openCount > 0 ? (
        <InputLine title="Actions">
          <button
            type="button"
            disabled={verifyingAll}
            onClick={handleVerifyAll}
            className="bg-primary text-white rounded-md p-1 px-2 h-8 mt-3 flex flex-row items-center justify-center text-xs gap-1 hover:opacity-80 disabled:opacity-50 transition-all"
          >
            {verifyingAll ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <TextSearch size={16} />
            )}
            Reconcile all ({openCount} open)
          </button>
        </InputLine>
      ) : null}

      <table className="w-full text-xs">
        <thead className="sticky top-0 border-b">
          <tr className="bg-secondary h-8">
            <th className="w-12">Dir</th>
            <th>Date</th>
            <th>Description</th>
            <th>Amount</th>
            <th>Reconciled With</th>
            {!isClosed ? <th className="w-16">Actions</th> : null}
          </tr>
        </thead>
        <tbody className="align-center">
          {items.map((item) => {
            const status = itemStatus[item.id] || item.status;
            const isVerified = status === "Verified";
            const isReview = status === "Review";
            const isOnlyBank = status === "OnlyBank";
            const isLoading = status === "loading";
            const isCredit = item.type === "Credit";
            const target = conciliationLabel(item);

            return (
              <tr key={item.id} className="border-b">
                <td className="py-1">
                  <div
                    className={`flex flex-row items-center justify-center font-bold ${
                      isCredit ? "text-emerald-600" : "text-red-600"
                    }`}
                  >
                    {isCredit ? "IN" : "OUT"}
                  </div>
                </td>
                <td>
                  <div className="flex flex-row items-center justify-center">
                    {safeDate(item.date)}
                  </div>
                </td>
                <td>
                  <div className="flex flex-row items-center justify-start px-2">
                    {item.description || "-"}
                  </div>
                </td>
                <td>
                  <div
                    className={`flex flex-row items-center justify-center font-bold ${
                      isCredit ? "text-emerald-600" : "text-red-600"
                    }`}
                  >
                    {formatter.format(Number(item.amount || 0))}
                  </div>
                </td>
                <td>
                  <div className="flex flex-row items-center justify-center gap-1">
                    {isLoading ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : isVerified ? (
                      <>
                        {item.manual ? (
                          <User size={12} className="text-gray-400" />
                        ) : (
                          <Sparkles size={12} className="text-gray-400" />
                        )}
                        <span className="text-emerald-600">
                          {target || "Verified"}
                        </span>
                      </>
                    ) : isReview ? (
                      <span className="flex flex-row items-center gap-1 text-orange-600 font-bold">
                        <AlertCircle size={12} />
                        Review
                        {item.matches ? ` (${item.matches})` : ""}
                      </span>
                    ) : isOnlyBank ? (
                      <span
                        className="flex flex-row items-center gap-1 text-blue-600"
                        title={item.memo || "Only Bank"}
                      >
                        <Landmark size={12} />
                        Only Bank
                      </span>
                    ) : (
                      <span className="text-gray-500">No match</span>
                    )}
                  </div>
                </td>
                {!isClosed ? (
                  <td>
                    <div className="flex flex-row items-center justify-center gap-2">
                      {isLoading ? null : isVerified || isOnlyBank ? (
                        <button
                          type="button"
                          title={
                            isOnlyBank
                              ? "Undo Only Bank"
                              : "Undo reconciliation"
                          }
                          onClick={() => handleUnlink(item)}
                          className="hover:scale-125 transition-all"
                        >
                          <X size={14} />
                        </button>
                      ) : isReview ? (
                        <button
                          type="button"
                          title="Choose between the candidates"
                          disabled={verifyingAll}
                          onClick={() => setMatchesItem(item)}
                          className="text-orange-600 hover:scale-125 transition-all disabled:opacity-40"
                        >
                          <Search size={14} />
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            title="Try automatic match"
                            disabled={verifyingAll}
                            onClick={() => handleVerifyItem(item)}
                            className="hover:scale-125 transition-all disabled:opacity-40"
                          >
                            <TextSearch size={14} />
                          </button>
                          <button
                            type="button"
                            title="Reconcile manually"
                            disabled={verifyingAll}
                            onClick={() => setMatchesItem(item)}
                            className="hover:scale-125 transition-all disabled:opacity-40"
                          >
                            <Search size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </InputLineGroup>
  );
}
